import React, { useCallback, useEffect, useReducer, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';

import {
  Button,
  Select,
  Input,
  Checkbox,
  SVGIcon,
} from '@pearsontechnology/aero-components';

import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import Modal, { ModalActions } from 'components/Modal';
import Circular from 'components/ProgressBar/Circular';
import { featureKeys } from 'services/FeatureFlagService';
import { publishFormat, publishType, status, tableOfContents } from '../../utils/constants';
import StatusInfo from './ExportStatusInfo';
import LandmarkModals from './LandmarkModals';
import messages from './messages';
import {
  FormField,
  InfoCard,
  Note,
  InfoCardList,
  InfoCardLabel,
  InputLableWrapper,
  CircularProgress,
  LandmarkEntry,
  Checkmark,
  AccessibilityNote,
  InfoIcon,
  AugmentedModalContent,
  Asterisk,
  PublicationModalFooter,
  RequiredAttribute,
  PublicationModalTooltip as Tooltip,
  TocItem,
  TocLabel,
  TocFeedback,
} from './styles';
import enhanceWithFeatureFlagSubscription from 'hocs/enhanceWithFeatureFlagSubscription';
import { compose } from 'redux';

const modalTitles = {
  entry: messages.modalTitleForEntry.defaultMessage,
  brief: messages.modalTitleForBrief.defaultMessage,
  start: messages.modalTitleForStart.defaultMessage,
  backmatter: messages.modalTitleForBackmatter.defaultMessage,
};

const initialState = {
  format: '',
  isbn: '',
  childISBN: '',
  type: '',
  note: false,
  isbnFeedback: false,
  typeFeedback: false,
  noteFeedback: false,
  showProgress: false,
  percentage: 0,
  metadata: {
    landmarks: {
      brief: "",
      start: "",
      backmatter: "",
      locations: ['brief', 'start', 'backmatter'],
      index: 0,
    },
  },
  modalTitle: modalTitles.entry,
  tocLevel: '',
  tocLevelFeedback: false,
  disablePublishButton: false,
};

const actionType = Object.freeze({
  INIT: 'init',
  FORMAT: 'format',
  ISBN: 'isbn',
  CHILD_ISBN: 'childISBN',
  TYPE: 'type',
  NOTE: 'note',
  ISBN_FEEDBACK: 'isbnFeedback',
  TYPE_FEEDBACK: 'typeFeedback',
  NOTE_FEEDBACK: 'noteFeedback',
  SHOW_PROGRESS: 'showProgress',
  PERCENTAGE: 'percentage',
  METADATA: 'metadata',
  SET_MODAL_TITLE: 'modalTitle',
  TOC_LEVEL: 'tocLevel',
  TOC_LEVEL_FEEDBACK: 'tocLevelFeedback',
  DISABLE_PUBLISH_BUTTON: 'disablePublishButton',
});

const reducer = (state, action) => {
  if (action.type === actionType.INIT) {
    return initialState;
  }
  const result = { ...state };
  result[action.type] = action.value;
  return result;
};

const tocOptions = Object.values(tableOfContents);

const ExportConfirmModal = ({
  visible,
  outputType,
  market,
  onPublish,
  onDismiss,
  theme,
  exportStatus,
  exportSnippetsCompleted = 0,
  totalExportSnippet = 0,
  publicationToPublish,
  getFeatureFlagValue,
  disabledTocLevel,
}) => {
  const isChannelPartnerFFEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_PUBLICATION_CHANNEL_PARTNER);
  const isTocOptionsFFEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_PUBLICATION_THREE_LEVEL_TOC_OPTION);
  const childISBNFFEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_PUBLICATION_CHILD_ISBN);
  const startingState = disabledTocLevel === '' ? initialState : { ...initialState, tocLevel: disabledTocLevel };
  const [state, dispatch] = useReducer(reducer, startingState);
  const {
    format,
    isbn,
    childISBN,
    type,
    note,
    isbnFeedback,
    typeFeedback,
    noteFeedback,
    showProgress,
    percentage,
    metadata,
    modalTitle,
    tocLevel,
    tocLevelFeedback,
    disablePublishButton,
  } = state;

  const [showToolTip, setShowToolTip] = useState(false);
  const [showLandmarkModals, setShowLandmarkModals] = useState(false);
  const [areLandmarksValid, setAreLandmarksValid] = useState(false);
  const [isIsbnSkipped, setIsIsbnSkipped] = useState(false);
  const [isValidationSkipped, setIsValidationSkipped] = useState(false);

  const tooltipRef = useRef(null);
  const publicationID = publicationToPublish?.activityId;

  useEffect(() => {
    if (isTocOptionsFFEnabled && disabledTocLevel != '') {
      dispatch({ type: actionType.TOC_LEVEL, value: disabledTocLevel });
      dispatch({ type: actionType.METADATA, value: { ...state.metadata, tocLevel: disabledTocLevel } });
      dispatch({ type: actionType.TOC_LEVEL_FEEDBACK, value: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTocOptionsFFEnabled, disabledTocLevel]);

  const reinitialize = useCallback(() => {
    dispatch({ type: actionType.INIT });
    dispatch({ type: actionType.FORMAT, value: outputType || publishFormat.EPUB });
  }, [outputType]);

  /**
   * Filter Publish formats based on feature flag
   */
  const filteredPublishFormats = (() => {
    const publishFormatCopy = { ...publishFormat };
    if (!isChannelPartnerFFEnabled) {
      delete publishFormatCopy.CHANNEL_PARTNER;
    }
    return publishFormatCopy;
  })();

  /**
   * @description handles format change action
   */
  const formatChange = (event) => {
    dispatch({ type: actionType.FORMAT, value: event.currentTarget.value });
  };

  /**
  * @description handles publication isbn change action
  */
  const childisbnChange = (event) => {
    const trimmedValue = event.currentTarget.value.trim();
    dispatch({ type: actionType.CHILD_ISBN, value: trimmedValue });
    dispatch({ type: actionType.METADATA, value: { ...state.metadata, childISBN: trimmedValue } });
  };

  const isbnChange = (event) => {
    const trimmedValue = event.currentTarget.value.trim();
    dispatch({ type: actionType.ISBN, value: trimmedValue });
    dispatch({ type: actionType.METADATA, value: { ...state.metadata, isbn: trimmedValue } });
    dispatch({ type: actionType.ISBN_FEEDBACK, value: false });
  };

  /**
 * @description handles type change action
 */
  const typeChange = (event) => {
    dispatch({ type: actionType.TYPE, value: event.currentTarget.value });
    dispatch({ type: actionType.TYPE_FEEDBACK, value: false });
  };

  /**
  * @description handles toc level change action
  */
  const tocLevelChange = (event) => {
    dispatch({ type: actionType.TOC_LEVEL, value: event.currentTarget.value });
    dispatch({ type: actionType.METADATA, value: { ...state.metadata, tocLevel: event.currentTarget.value } });
    dispatch({ type: actionType.TOC_LEVEL_FEEDBACK, value: false });
  };

  /**
  * @description handles publication note change action
  */
  const noteChange = (event) => {
    dispatch({ type: actionType.NOTE, value: event.currentTarget.value });
    dispatch({ type: actionType.NOTE_FEEDBACK, value: false });
  };

  /**
  * @description reset landmark values
  */
  const resetLandmarkValues = () => {
    const landmarks = initialState.metadata?.landmarks;
    if (landmarks) {
      landmarks.brief = '';
      landmarks.start = '';
      landmarks.backmatter = '';
      landmarks.index = 0;
    }
  };

  const isbnSkipHandler = () => {
    setIsIsbnSkipped(!isIsbnSkipped);
    if (childISBNFFEnabled) {
      // When switching ISBN on/off, clear out both ISBN and childISBN - we don't want backend getting old values
      dispatch({ type: actionType.ISBN, value: '' });
      dispatch({ type: actionType.CHILD_ISBN, value: '' });
      dispatch({ type: actionType.METADATA, value: { ...state.metadata, isbn: '', childISBN: '' } });
    }
  };

  const validationSkipHandler = () => {
    setIsValidationSkipped(!isValidationSkipped);
  };

  /**
   * @description handles re-initialization and modal dismiss
   */
  const handleDismiss = useCallback(() => {
    //reset all local state
    setShowToolTip(false);
    setShowLandmarkModals(false);
    setAreLandmarksValid(false);
    setIsIsbnSkipped(false);
    setIsValidationSkipped(false);
    //reset the reducer to initialState
    reinitialize();
    //we need to reset landmarks manually as we are doing shallow copy in reducer.
    resetLandmarkValues();
    //close the modal
    onDismiss();
  }, [reinitialize, onDismiss]);

  /**
   * @description updates progress bar until export is complete
   */
  const handleCountdown = useCallback((percent) => {
    if (percent) {
      dispatch({ type: actionType.PERCENTAGE, value: percent });
    }
    setTimeout(() => {
      handleDismiss();
    }, 1000);
  }, [handleDismiss]);


  const handlePublish = () => {
    /* Added to disable the publish button once clicked to avoid multiple publish call. */
    dispatch({ type: actionType.DISABLE_PUBLISH_BUTTON, value: true });

    if (!validateFields()) {
      metadata.isbn = isbn;
      metadata.childISBN = childISBN;
      metadata.isIsbnSkipped = isIsbnSkipped;
      metadata.isValidationSkipped = isValidationSkipped;
      setIsIsbnSkipped(false);
      setIsValidationSkipped(false);

      // TODO: 'requestIdleCallback' can be removed once publication page is refactored
      requestIdleCallback(() => {
        onPublish({
          format,
          type,
          note,
          metadata,
          ...(market && { market }),
          ...(isTocOptionsFFEnabled && { tocLevel }),
        });
      });
    } else {
      /* Enable publish button when validation fails*/
      dispatch({ type: actionType.DISABLE_PUBLISH_BUTTON, value: false });
      return;
    }
  };

  const validateFields = () => {
    let missingField = false;
    if (!type) {
      missingField = true;
      dispatch({ type: actionType.TYPE_FEEDBACK, value: true });
    }
    if (showTocLevelField && !tocLevel) {
      missingField = true;
      dispatch({ type: actionType.TOC_LEVEL_FEEDBACK, value: true });
    }
    if (!note) {
      missingField = true;
      dispatch({ type: actionType.NOTE_FEEDBACK, value: true });
    }
    if (!isIsbnSkipped && format === filteredPublishFormats.CHANNEL_PARTNER && !isbn) {
      missingField = true;
      dispatch({ type: actionType.ISBN_FEEDBACK, value: true });
    }
    return missingField;
  };

  const tooltipHandler = (event) => {
    event.stopPropagation();
    setShowToolTip(true);
  };

  const tooltipClose = (event) => {
    event.stopPropagation();
    setShowToolTip(false);
  };

  const launchLandmarkModals = (event) => {
    event.stopPropagation();
    dispatch({ type: actionType.SET_MODAL_TITLE, value: modalTitles['brief'] });
    setShowLandmarkModals(true);
  };

  useEffect(() => {
    let timer;
    switch (exportStatus) {
      case status.INPROGRESS: {
        const completedPercentage = Math.floor((exportSnippetsCompleted / totalExportSnippet || 0) * 100);
        dispatch({ type: actionType.SHOW_PROGRESS, value: true });
        dispatch({ type: actionType.MODAL_TITLE, value: intl.formatMessage(messages.exportTitle) });
        dispatch({ type: actionType.PERCENTAGE, value: completedPercentage });
        break;
      }
      case status.COMPLETED:
        handleCountdown(100);
        break;
      case status.FAILED:
        handleCountdown();
        break;
      default:
        break;
    }
    return () => { clearInterval(timer); };
  }, [exportStatus, handleCountdown, exportSnippetsCompleted, totalExportSnippet]);


  useEffect(() => {
    dispatch({ type: actionType.FORMAT, value: outputType || format || publishFormat.EPUB });
  }, [outputType, format]);

  const landmarkEntryMarkup = () => (
    <LandmarkEntry>
      <AccessibilityNote onClick={launchLandmarkModals}>{intl.formatMessage(messages.accessibilityNote)}<Asterisk > *</Asterisk></AccessibilityNote>
      <InfoIcon onMouseEnter={tooltipHandler} onMouseLeave={tooltipClose}>
        <Tooltip
          showToolTip={showToolTip}
          placement={'bottom'}
          positionOffset={{ top: '230px', left: 0 }}
          ref={tooltipRef}
        >
          {intl.formatMessage(messages.landmarkInfo)}
        </Tooltip>
      </InfoIcon>
      {areLandmarksValid && <Checkmark></Checkmark>}
    </LandmarkEntry>
  );

  const showFormatSelect = !outputType;
  const isChannelPartner = outputType === publishFormat.CHANNEL_PARTNER || format === publishFormat.CHANNEL_PARTNER;
  const isIndesignPublish = outputType === publishFormat.IN_DESIGN || format === publishFormat.IN_DESIGN;
  const isPublishButtonDisabled = isChannelPartner ? (!areLandmarksValid || (!isbn && !isIsbnSkipped)) :
    (isIndesignPublish ? !areLandmarksValid : false);
  const showTocLevelField = isTocOptionsFFEnabled && (outputType === publishFormat.EPUB);

  return (
    <Modal icon="duotonePublications" visible={visible} onDismiss={handleDismiss} title={modalTitle}>
      {!showProgress ?
        <AugmentedModalContent>
          {showLandmarkModals ?
            <LandmarkModals
              publicationID={publicationID}
              setShowLandmarkModals={setShowLandmarkModals}
              exportConfirmState={state}
              setExportConfirmState={dispatch}
              modalTitles={modalTitles}
              setAreLandmarksValid={setAreLandmarksValid}
              actionType={actionType} />
            :
            <div>
              <FormField>
                {showFormatSelect &&
                  <Select
                    label={intl.formatMessage(messages.format)}
                    defaultValue={format}
                    onChange={formatChange}
                  >
                    {Object.keys(filteredPublishFormats).map((key) => (<option
                      key={filteredPublishFormats[key]}
                      value={filteredPublishFormats[key]}
                      data-testid="format-option"
                    >
                      {intl.formatMessage(messages[filteredPublishFormats[key]])}
                    </option>))}
                  </Select>
                }
                {isChannelPartner &&
                  <>
                    <Input.Label>{intl.formatMessage(messages.isbnValidationLable)}</Input.Label>
                    <FormField>
                      <Input
                        label={intl.formatMessage(messages.isbn)}
                        asterisk={isIsbnSkipped ? false : true}
                        placeholder={isIsbnSkipped ? '' : intl.formatMessage(messages.isbnPlaceholder)}
                        onChange={isbnChange}
                        feedback={isbnFeedback ? intl.formatMessage(messages.isbnFeedback) : ''}
                        value={isIsbnSkipped ? '' : isbn}
                        disabled={isIsbnSkipped ? true : false}
                      />
                    </FormField>
                  </>
                }
                {isChannelPartner &&
                  <>
                    <Checkbox
                      label={intl.formatMessage(messages.isbnCheckLable)}
                      checked={isIsbnSkipped}
                      onChange={isbnSkipHandler}
                      value={intl.formatMessage(messages.isbnCheckLable)}
                    />
                    <InputLableWrapper>
                      <Input.Label>{intl.formatMessage(messages.isbnCheckText)}</Input.Label>
                    </InputLableWrapper>
                    {
                      (childISBNFFEnabled && isIsbnSkipped) && <FormField>
                        <Input
                          label={intl.formatMessage(messages.childisbn)}
                          value={isIsbnSkipped ? childISBN : ''}
                          onChange={childisbnChange}
                          placeholder={intl.formatMessage(messages.childIsbnPlaceholder)}
                        />
                      </FormField>
                    }

                  </>
                }
                {isChannelPartner &&
                  <>
                    <Checkbox
                      label={intl.formatMessage(messages.validationEnabledLable)}
                      checked={isValidationSkipped}
                      onChange={validationSkipHandler}
                      value={intl.formatMessage(messages.validationEnabledLable)}
                    />
                    <InputLableWrapper>
                      <Input.Label>{intl.formatMessage(messages.validationText)}</Input.Label>
                    </InputLableWrapper>
                  </>
                }
                {isChannelPartner && landmarkEntryMarkup()}
              </FormField>
              <FormField>
                <Select
                  label={intl.formatMessage(messages.publishType)}
                  feedback={typeFeedback ? intl.formatMessage(messages.typeFeedback) : ''}
                  defaultValue={type}
                  onChange={typeChange}
                >
                  <option hidden disabled value=""></option>
                  {Object.keys(publishType).map((key) => (
                    <option
                      key={publishType[key]}
                      value={intl.formatMessage(messages[publishType[key]])}
                      data-testid="type-option"
                    >
                      {intl.formatMessage(messages[publishType[key]])}
                    </option>)
                  )}
                </Select>
              </FormField>
              {isIndesignPublish && <FormField>{landmarkEntryMarkup()}</FormField>}
              {
                showTocLevelField && <FormField>
                  <TocLabel>{intl.formatMessage(messages.organizationalLevel)}</TocLabel>
                  {tocOptions.map((toc) => (
                    <TocItem key={toc}>
                      <Checkbox
                        label={toc}
                        checked={disabledTocLevel ? disabledTocLevel === toc : tocLevel === toc || false}
                        rounded={true}
                        onChange={tocLevelChange}
                        value={toc}
                        disabled={disabledTocLevel ? true : false}
                      />
                    </TocItem>
                  ))}
                  {
                    tocLevelFeedback &&
                    <TocFeedback>
                      <SVGIcon icon='warningCircle' />
                      {intl.formatMessage(messages.tocFeedback)}
                    </TocFeedback>
                  }
                </FormField>
              }
              <FormField>
                <Note
                  multiline
                  label={intl.formatMessage(messages.note)}
                  placeholder={intl.formatMessage(messages.publishComment)}
                  onChange={noteChange}
                  feedback={noteFeedback ? intl.formatMessage(messages.noteFeedback) : ''}
                  value={state.note ? state.note : ""}
                />
              </FormField>

              <InfoCard>
                <InfoCardLabel>{intl.formatMessage(messages.publishCardInfo)}</InfoCardLabel>
                <InfoCardList>
                  <li>{intl.formatMessage(messages.publishCardInfoUpdate)}</li>
                  <li>{intl.formatMessage(messages.publishCardInfoRefresh)}</li>
                </InfoCardList>
              </InfoCard>

              <PublicationModalFooter format={format}>
                <ModalActions scrim>
                  {(isChannelPartner ||
                    isIndesignPublish) &&
                    <RequiredAttribute >
                      *Required
                    </RequiredAttribute>
                  }
                  <Button grey onClick={handleDismiss}>{intl.formatMessage(messages.cancelButton)}</Button>
                  <Button primary disabled={isPublishButtonDisabled || disablePublishButton} onClick={handlePublish}>{intl.formatMessage(messages.publish)}</Button>
                </ModalActions>
              </PublicationModalFooter>
            </div>
          }
        </AugmentedModalContent>
        :
        <div>
          <AugmentedModalContent>
            <CircularProgress>
              <Circular key={percentage} percentage={percentage} radius={78} trackWidth={12} trackColor={theme['color.greyDark']} progressFillColor={theme['color.greenLight']} />
              <StatusInfo exportStatus={exportStatus} />
            </CircularProgress>
          </AugmentedModalContent>
        </div>
      }
    </Modal >);
};

ExportConfirmModal.propTypes = {
  onDismiss: PropTypes.func,
  visible: PropTypes.bool,
  onPublish: PropTypes.func,
  theme: PropTypes.object,
  outputType: PropTypes.string,
  market: PropTypes.string,
  exportStatus: PropTypes.string,
  exportSnippetsCompleted: PropTypes.number,
  totalExportSnippet: PropTypes.number,
  publicationToPublish: PropTypes.object,
};

const flagNames = [featureKeys.WORKSPACE_PUBLICATION_CHANNEL_PARTNER, featureKeys.WORKSPACE_PUBLICATION_THREE_LEVEL_TOC_OPTION, featureKeys.WORKSPACE_PUBLICATION_CHILD_ISBN];
export default compose(withTheme, enhanceWithFeatureFlagSubscription)(ExportConfirmModal, flagNames);
